import React, { FunctionComponent, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import capitalize from 'lodash/capitalize'
import reduce from 'lodash/reduce'
import LoaderButton from '../../../components/loaderButton'
import LoanOverviewRow from './loanOverviewRow'
import { displayDollars } from '../../../helpers/displayFormatHelper'
import {
  selectCurrentPaymentOptions,
  selectCurrentRefi,
  selectCurrentRefiUser,
  selectSelectedGapProduct,
  selectSelectedVscProducts,
} from '../../../reducers'
import { acceptRefi, getRefi, getRefiPaymentOptions } from '../refiActions'
import { Refi } from '../types'
import { Callback } from '../../../utils/types'
import { User } from '../../../core/types'
import dateHelpers from '../../../helpers/dateHelpers'
import { Product } from '../../refiTransactions/types'

export interface Props {
  paymentOptions: any[]
  refi: Refi
  user: User
  acceptRefi: Callback<Promise<any>>
  getRefi: Callback<Promise<any>>
  getRefiPaymentOptions: Callback<Promise<any>>
  selectedVscProducts: Product[]
  selectedGapProduct: Product
}

const formatMonths = (value: any) => {
  return `${String(value)} months`
}
const formatPct = (value: any) => {
  return `${String(value)}%`
}

export const ReviewAndConfirmLoan: FunctionComponent<Props> = ({
  paymentOptions,
  refi,
  user,
  acceptRefi,
  getRefi,
  getRefiPaymentOptions,
  selectedVscProducts,
  selectedGapProduct,
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const { id, cash_back_amount } = refi

  useEffect(() => {
    getRefi(id)
    getRefiPaymentOptions({ id, cash_back_amount })
  }, [])

  const loanDetails = {
    id,
    interest_rate: paymentOptions[0]
      ? paymentOptions[0].rate
      : refi.interest_rate,
    current_loan_payoff: refi.current_loan_payoff,
    fees: refi.fees,
    payment: paymentOptions[0]
      ? paymentOptions[0].payment
      : refi.monthly_payment,
    term: paymentOptions[0] ? paymentOptions[0].term : refi.term,
    termRemaining:
      refi.current_loan_term_remaining ||
      dateHelpers.calculateTimeRemaining(
        paymentOptions[0] ? paymentOptions[0].term : refi.term,
        refi.current_loan_date
      ),
    total_amount: refi.total_amount,
  }

  const onClickSubmit = () => {
    setIsLoading(true)
    acceptRefi({ id })
      .then(() => {
        window.location.href = `users/${user.id}`
      })
      .catch(() => setIsLoading(false))
  }

  const protectionProductsValue = reduce(
    selectedVscProducts,
    (sum, p) => {
      const selectedOption = p.options.find(
        (o) => o.id === p.selected_option_id
      )
      return sum + Number(selectedOption?.total_cost || 0)
    },
    0
  )

  const gapInsuranceValue =
    selectedGapProduct && selectedGapProduct.options[0].total_cost

  return (
    <div className="form form--with-border loan-details">
      <p className="loan-details__title">
        Review and confirm the terms of your request
      </p>
      <p className="loan-details__subtitle">New loan details</p>
      <LoanOverviewRow
        title="Estimated current loan balance"
        value={displayDollars(loanDetails.current_loan_payoff)}
      />

      {(loanDetails.fees || []).map((fee) => (
        <div className="loan-details__row" key={fee.description}>
          <span>{capitalize(fee.description)}</span>
          <span className="loan-details__value">
            {displayDollars(fee.amount)}
          </span>
        </div>
      ))}

      <div className="loan-details__row" key="protection-products">
        <span>Protection products</span>
        <span className="loan-details__value">
          {displayDollars(protectionProductsValue)}
        </span>
      </div>

      {gapInsuranceValue && (
        <div className="loan-details__row" key="protection-products">
          <span>GAP Insurance</span>
          <span className="loan-details__value">
            {displayDollars(gapInsuranceValue)}
          </span>
        </div>
      )}

      <LoanOverviewRow
        title="Estimated amount financed"
        value={displayDollars(loanDetails.total_amount)}
      />

      <p className="loan-details__subtitle loan-details__subtitle--small-margin-top">
        Payment details
      </p>
      <LoanOverviewRow
        title="New monthly payment"
        value={displayDollars(loanDetails.payment)}
      />
      <LoanOverviewRow
        title="Estimated APR"
        value={formatPct(loanDetails.interest_rate)}
      />
      <LoanOverviewRow
        title="Loan term"
        value={formatMonths(loanDetails.term)}
      />

      <div className="form__group refi-app__form-buttons">
        <LoaderButton
          type="button"
          className="button button-drive-america"
          onClick={onClickSubmit}
          isLoading={isLoading}
        >
          Submit
        </LoaderButton>
      </div>
    </div>
  )
}

export const mapStateToProps = (state) => ({
  paymentOptions: selectCurrentPaymentOptions(state),
  refi: selectCurrentRefi(state),
  selectedVscProducts: selectSelectedVscProducts(state),
  selectedGapProduct: selectSelectedGapProduct(state),
  user: selectCurrentRefiUser(state),
})

export default connect(mapStateToProps, {
  acceptRefi,
  getRefi,
  getRefiPaymentOptions,
})(ReviewAndConfirmLoan)
